import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Sparkles,
  RefreshCw,
  HardDrive,
  AlertCircle,
  CheckCircle,
  TrendingDown,
  FileImage
} from 'lucide-react'

interface MediaOptimizationRecommendation {
  image_id: string
  filename: string
  current_size: number
  potential_savings: number
  recommendation_type: 'compress' | 'resize' | 'unused'
  details: string
  priority: 'high' | 'medium' | 'low'
}

interface MediaOptimizationsResponse {
  total_images: number
  total_current_size: number
  total_potential_savings: number
  recommendations: MediaOptimizationRecommendation[]
}

export default function BlogMediaOptimization() {
  // Fetch optimization recommendations
  const {
    data: optimizations,
    isLoading,
    error,
    refetch,
  } = useQuery<MediaOptimizationsResponse>({
    queryKey: ['blog-media-optimizations'],
    queryFn: async () => {
      const response = await apiService.blog.images.optimizationRecommendations()
      return response as MediaOptimizationsResponse
    },
  })

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  // Get priority badge variant
  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'destructive'
      case 'medium':
        return 'default'
      case 'low':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  // Get recommendation type icon
  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'compress':
        return <TrendingDown className="h-4 w-4" />
      case 'resize':
        return <FileImage className="h-4 w-4" />
      case 'unused':
        return <AlertCircle className="h-4 w-4" />
      default:
        return <Sparkles className="h-4 w-4" />
    }
  }

  // Get recommendation type label
  const getRecommendationLabel = (type: string) => {
    switch (type) {
      case 'compress':
        return 'Compression'
      case 'resize':
        return 'Resize'
      case 'unused':
        return 'Unused'
      default:
        return type
    }
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="bg-card text-card-foreground border-border">
          <CardContent className="pt-6">
            <p className="text-destructive">
              Error loading optimization recommendations: {error.message}
            </p>
            <Button onClick={() => refetch()} className="mt-4">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Media Optimization
              </h1>
              <p className="text-sm text-muted-foreground">
                Analyze and optimize your blog media library
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="border-border hover:bg-accent"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
              />
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <RefreshCw className="mx-auto h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-muted-foreground">
                Analyzing media library...
              </p>
            </div>
          </div>
        ) : optimizations ? (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="bg-card text-card-foreground border-border">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Images</CardTitle>
                  <FileImage className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground">
                    {optimizations.total_images}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Analyzed for optimization
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card text-card-foreground border-border">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Current Size</CardTitle>
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground">
                    {formatFileSize(optimizations.total_current_size)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total media library size
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card text-card-foreground border-border">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Potential Savings</CardTitle>
                  <TrendingDown className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {formatFileSize(optimizations.total_potential_savings)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {optimizations.recommendations.length} recommendations
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recommendations List */}
            {optimizations.recommendations.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground">
                    Optimization Recommendations
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {optimizations.recommendations.length} item
                    {optimizations.recommendations.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="space-y-3">
                  {optimizations.recommendations.map((recommendation) => (
                    <Card
                      key={recommendation.image_id}
                      className="bg-card text-card-foreground border-border hover:shadow-md transition-shadow"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2 text-primary">
                                {getRecommendationIcon(recommendation.recommendation_type)}
                                <span className="font-medium text-sm">
                                  {getRecommendationLabel(recommendation.recommendation_type)}
                                </span>
                              </div>
                              <Badge variant={getPriorityVariant(recommendation.priority)}>
                                {recommendation.priority.toUpperCase()} PRIORITY
                              </Badge>
                            </div>

                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {recommendation.filename}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {recommendation.details}
                              </p>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>
                                Current: {formatFileSize(recommendation.current_size)}
                              </span>
                              <span>•</span>
                              <span className="text-green-600 dark:text-green-400">
                                Save: {formatFileSize(recommendation.potential_savings)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-64 flex-col items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-500" />
                <p className="mt-4 text-lg font-medium text-foreground">
                  All Optimized!
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your media library is fully optimized
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
